import { Plus, AlertTriangle } from "lucide-react";
import { isSameDay, format, getDay } from "date-fns";
import { getCapacityColor, hasConflict, GRID_CELL_BG, WEEKEND_BG } from "./constants";
import AssignmentBlock from "./AssignmentBlock";

export default function DayCell({
  date, userId, assignments, totalHours,
  onAddClick, onAssignmentClick, onCellSelect,
  onDragStart, onDragEnd, onDragOver, onDrop,
  onResizeStart, onFillHandleStart,
  isWeekendDay, isSaturday, isSunday,
  isToday, isWeekEnd,
  isHoliday, holidayName,
  dragOverDate, dragOverUserId, isValidDrop,
  resizingAssignment, resizePreviewHours,
  modifiedIds, newIds, deletedIds,
  hasBlockedStatus, showSollIst,
  isFillDragging, fillPreviewCell,
  isSelected, selectedIds, onSelectAssignment, onQuickDelete,
  dayWidth, rowHeight, zoom,
  onOpenInTimesheet,
}) {
  const hasConflictState = hasConflict(totalHours);
  const isDropTarget = dragOverDate && isSameDay(dragOverDate, date) && dragOverUserId === userId;
  const isFillPreview = fillPreviewCell && isSameDay(fillPreviewCell.date, date) && fillPreviewCell.userId === userId;
  const isCompact = zoom === "compact";

  // Cell background - DARKER grid for card contrast
  let bg = GRID_CELL_BG;
  if (isSaturday || isSunday) bg = WEEKEND_BG;
  if (isHoliday) bg = "rgba(245, 158, 11, 0.10)";
  if (isToday) bg = "var(--tga-blue-soft)";
  if (hasConflictState) bg = "var(--tga-red-soft)";
  if (hasBlockedStatus) bg = "var(--tga-purple-soft)";

  // Border
  const borderR = isWeekEnd
    ? "2px solid var(--tga-border-strong)"
    : "1px solid var(--tga-border)";

  // Drop/Selection highlight
  let outline = "none";
  if (isDropTarget) outline = isValidDrop ? "2px solid rgba(74,222,128,0.4)" : "2px solid rgba(248,113,113,0.4)";
  else if (isFillPreview) outline = "2px solid rgba(96,165,250,0.4)";
  else if (isSelected && !isDropTarget) outline = "2px solid rgba(96,165,250,0.25)";

  const hasAssignment = assignments.length > 0;

  return (
    <div
      className="flex-shrink-0 relative"
      style={{
        width: dayWidth,
        height: rowHeight,
        backgroundColor: bg,
        borderBottom: "1px solid var(--tga-border)",
        borderRight: borderR,
        outline,
        outlineOffset: "-2px",
        transition: "background-color 100ms ease",
      }}
      onDragOver={(e) => onDragOver(e, date, userId)}
      onDrop={(e) => onDrop(e, date, userId)}
      onClick={(e) => {
        e.stopPropagation();
        if (!hasAssignment) {
          onCellSelect(date, userId);
          if (selectedIds?.size > 0 && !e.ctrlKey && !e.metaKey) onSelectAssignment?.(null, false);
        }
      }}
      onDoubleClick={(e) => { e.stopPropagation(); if (!hasAssignment) onAddClick(date, userId); }}
      data-date={format(date, "yyyy-MM-dd")}
      data-user={userId}
      title={isHoliday ? `Feiertag: ${holidayName}` : undefined}
    >
      {/* Today marker */}
      {isToday && <div className="absolute top-0 left-0 right-0 h-[2px] z-20" style={{ background: "var(--tga-blue)" }} />}

      {/* Conflict warning */}
      {hasConflictState && (
        <div className="absolute top-1.5 right-1.5 z-20">
          <AlertTriangle className="w-4 h-4" style={{ color: "#F87171" }} title={`Ueberbuchung: ${totalHours}h`} />
        </div>
      )}

      {/* Assignment Card */}
      {hasAssignment ? (
        assignments.map((a) => (
          <AssignmentBlock
            key={a.id}
            assignment={a}
            onClick={onAssignmentClick}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onResizeStart={onResizeStart}
            onFillHandleStart={onFillHandleStart}
            isResizing={resizingAssignment?.id === a.id}
            resizePreviewHours={resizingAssignment?.id === a.id ? resizePreviewHours : null}
            isDragging={false}
            isModified={modifiedIds.has(a.id)}
            isNew={newIds.has(a.id)}
            isDeleted={deletedIds.has(a.id)}
            showSollIst={showSollIst}
            isFillDragging={isFillDragging}
            isSelected={selectedIds?.has(a.id)}
            onSelect={onSelectAssignment}
            onQuickDelete={onQuickDelete}
            onOpenInTimesheet={onOpenInTimesheet}
            zoom={zoom}
          />
        ))
      ) : (
        /* Empty cell - CLEAR plus button */
        <div className="absolute inset-0 flex items-center justify-center cursor-cell group">
          <div
            className="flex items-center gap-1 rounded-lg transition-all duration-150 opacity-0 group-hover:opacity-100"
            style={{
              padding: isCompact ? "4px 8px" : "6px 12px",
              background: "var(--tga-border)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(59,130,246,0.12)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--tga-border)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Plus className={`${isCompact ? "w-3 h-3" : "w-4 h-4"}`} style={{ color: "#60A5FA" }} />
            {!isCompact && <span className="text-[11px] font-medium" style={{ color: "#60A5FA" }}>Einsatz</span>}
          </div>
        </div>
      )}

      {/* Capacity indicator */}
      {totalHours > 0 && !hasAssignment && (
        <div
          className="absolute bottom-1 right-1.5 text-[10px] font-mono font-bold z-10"
          style={{ color: getCapacityColor(totalHours) }}
        >
          {totalHours}h
        </div>
      )}

      {/* Fill preview */}
      {isFillPreview && !hasAssignment && (
        <div className="absolute inset-1 flex items-center justify-center pointer-events-none rounded-lg"
          style={{ border: "2px dashed rgba(96,165,250,0.4)", background: "rgba(59,130,246,0.06)" }}>
          <span className="text-[12px] font-semibold" style={{ color: "#60A5FA" }}>Kopie</span>
        </div>
      )}
    </div>
  );
}
